import {
  DateTemplates,
  formatDate,
} from '@robovision/rvai-utils'
import {
  addDays,
  intervalToDuration,
} from 'date-fns'
import { storeToRefs } from 'pinia'
import { LocalStorage } from 'quasar'
import {
  computed,
  ref,
} from 'vue'
import {
  LicenseInfoType,
  LicenseState,
} from '~domain/License'
import { LocalStorageKeys } from '~domain/localStorageKeys'
import { i18n } from '~i18n'
import { useLicenseStore } from '../useLicenseStore'


export function useLicensing () {
  const licenseStore = useLicenseStore()
  const { license } = storeToRefs(licenseStore)

  // TODO should we refresh this value occasionally?
  const now = ref(new Date().getTime())

  const expirationInterval = computed(() => {
    if (!license.value) return undefined
    const expirationTime = license.value.expiresAt.getTime()
    // Special-case: valid and expired at is 0 -> infinitely valid
    if (license.value.active && expirationTime === 0) {
      return Infinity
    }
    return license.value.expiresAt.getTime() - now.value
  })

  const expirationDuration = computed(() => {
    if (!expirationInterval.value) return undefined
    // 8.64e15 is the maximum time value in javascript/typescript
    return intervalToDuration({
      end: Math.min(8.64e15, Math.max(expirationInterval.value, 0)),
      start: 0,
    })
  })

  const licenseState = computed(() => {
    // License state logic:
    // - No license present -> INVALID
    // - License not active -> EXPIRED
    // - License active and 0 expiration time -> infinitely VALID
    // - License active and less than 1 month until expiration -> EXPIRING
    // - License active and valid for more than 1 month -> VALID
    const oneMonth = addDays(new Date(), 31).getTime()
    if (!license.value) {
      return LicenseState.INVALID
    } else if (!license.value.active) {
      return LicenseState.EXPIRED
    } else if (license.value.expiresAt.getTime() > 0 && license.value.expiresAt.getTime() <= oneMonth) {
      return LicenseState.EXPIRING
    } else {
      return LicenseState.VALID
    }
  })

  const isLicenseActive = computed(() => {
    return [LicenseState.EXPIRING, LicenseState.VALID].includes(licenseState.value)
  })

  const isLicenseExpired = computed(() => {
    return licenseState.value === LicenseState.EXPIRED
  })

  const isLicenseInvalid = computed(() => {
    return licenseState.value === LicenseState.INVALID
  })

  const isLicenseValid = computed(() => {
    return licenseState.value === LicenseState.VALID
  })

  const expiresAtFormatted = computed(() => {
    return license.value?.expiresAt
      ? formatDate(license.value.expiresAt, DateTemplates.Short)
      : i18n.t('general.text.never')
  })

  const expiresAtRelative = computed(() => {
    if (
      !expirationInterval.value ||
      expirationInterval.value < 0 ||
      !expirationDuration.value
    ) {
      return {
        count: 0,
        message: 'about.licensing.daysLeft',
      }
    } else if (expirationInterval.value === Infinity) {
      return {
        message: 'general.text.infinite',
      }
    } else if (expirationDuration.value.years) {
      return {
        count: expirationDuration.value.years,
        message: 'about.licensing.yearsLeft',
      }
    } else if (expirationDuration.value.months) {
      return {
        count: expirationDuration.value.months,
        message: 'about.licensing.monthsLeft',
      }
    } else {
      return {
        count: expirationDuration.value.days || 1,
        message: 'about.licensing.daysLeft',
      }
    }
  })

  const licenseColor = computed(() => {
    switch (licenseState.value) {
      case LicenseState.VALID:
        return 'positive'
      case LicenseState.EXPIRING:
        return 'warning'
      case LicenseState.INVALID:
      case LicenseState.EXPIRED:
      default:
        return 'negative'
    }
  })

  const licenseDescription = computed(() => {
    switch (licenseState.value) {
      case LicenseState.VALID:
        return i18n.t('about.licensing.licenseIsActive')
      case LicenseState.EXPIRING:
        return i18n.t('about.licensing.expiringSoon')
      case LicenseState.EXPIRED:
        return i18n.t('about.licensing.licenseIsExpired')
      case LicenseState.INVALID:
      default:
        return i18n.t('about.licensing.invalidLicense')
    }
  })

  const licenseIcon = computed(() => {
    switch (licenseState.value) {
      case LicenseState.VALID:
        return {
          color: 'positive',
          name: 'mdi-check-circle',
        }
      case LicenseState.EXPIRING:
        return {
          color: 'warning',
          name: 'mdi-alert-circle',
        }
      case LicenseState.INVALID:
      case LicenseState.EXPIRED:
      default:
        return {
          color: 'negative',
          name: 'mdi-alert-circle',
        }
    }
  })

  const licenseInfo = computed(() => {
    if (license.value?.licenseKey) {
      return {
        label: i18n.t('about.licensing.licenseKey'),
        type: LicenseInfoType.LICENSE_KEY,
        value: license.value?.licenseKey,
      }
    } else if (license.value?.machineReference) {
      return {
        label: i18n.t('about.licensing.machineName'),
        type: LicenseInfoType.MACHINE_REFERENCE,
        value: license.value?.machineReference,
      }
    } else {
      return undefined
    }
  })

  const licenseProgress = computed(() => {
    const oneMonth = addDays(new Date(), 31).getTime() - now.value
    switch (licenseState.value) {
      case LicenseState.VALID:
        return 1
      case LicenseState.EXPIRING:
        return expirationInterval.value ? expirationInterval.value / oneMonth : 0
      case LicenseState.EXPIRED:
      case LicenseState.INVALID:
      default:
        return 0
    }
  })

  function shouldDisplayLicenseDialog (): boolean {
    const isUserNotified = LocalStorage.getItem(LocalStorageKeys.LicenseNotificationShown)

    return !isUserNotified && [LicenseState.EXPIRED, LicenseState.EXPIRING].includes(licenseState.value)
  }

  function setLicenseNotificationShown (v: boolean): void {
    LocalStorage.set(LocalStorageKeys.LicenseNotificationShown, v)
  }

  return {
    expirationDuration,
    expirationInterval,
    expiresAtFormatted,
    expiresAtRelative,
    isLicenseActive,
    isLicenseExpired,
    isLicenseInvalid,
    isLicenseValid,
    licenseColor,
    licenseDescription,
    licenseIcon,
    licenseInfo,
    licenseProgress,
    licenseState,
    setLicenseNotificationShown,
    shouldDisplayLicenseDialog,
  }
}
